/**
 * Фабрика для создания регулярных выражений с возможностью указания флагов.
 * Позволяет создавать предварительно сконфигурированные RegExp-функции.
 *
 * @param {string | RegExp} pattern - Шаблон регулярного выражения (строка или RegExp)
 * @returns {Function} Функция, принимающая флаги и возвращающая RegExp
 * @example
 * const createDigitRegex = getRegexpFunction('\\d+');
 * createDigitRegex('g'); // => /\d+/g
 */
export const getRegexpFunction = (pattern: string | RegExp) => (flags: string = '') => new RegExp(pattern, flags);

/**
 * Регулярное выражение для проверки структуры пути (например: "prop.subProp.value")
 */
export const pathInStringStructure = getRegexpFunction(/\b\w+(?:\.\w+)*\b/);

/**
 * Регулярное выражение для проверки формата "название/иконка".
 * Допускает только буквы и цифры, разделенные слешем.
 *
 * @type {RegExp}
 * @example
 * titleNameAndIconName.test('user/profile'); // true
 * titleNameAndIconName.test('user profile'); // false
 */
export const titleNameAndIconName = getRegexpFunction(/^[a-zA-Z0-9]+\/[a-zA-Z0-9]+$/);

/**
 * Регулярное выражение для валидации CSS-значений ширины.
 * Поддерживает:
 * - Ключевые слова (auto, max-content и т.д.)
 * - Числовые значения с единицами измерения (px, em, vh и т.д.)
 * - Проценты
 *
 * @type {RegExp}
 * @example
 * widthRegExp.test('100px'); // true
 * widthRegExp.test('auto'); // true
 * widthRegExp.test('10.5rem'); // true
 * widthRegExp.test('abc'); // false
 */
export const widthRegExp = new RegExp(
    '^(?:auto|max-content|min-content|fit-content|inherit)$|'
  + '^(?:\\d+(?:em|vh|vw|px|mm|cm|rem|%)|\\d+\\.\\d+(?:em|vh|vw|px|mm|cm|rem|%)?)$',
);

/**
 * Простое регулярное выражение для проверки, что строка содержит только цифры.
 *
 * @type {RegExp}
 * @example
 * isNumberRegExp.test('12345'); // true
 * isNumberRegExp.test('12a45'); // false
 */
export const isNumberRegExp = /^\d+$/;

/**
 * Регулярное выражение для проверки URL-адресов.
 * Поддерживает:
 * - Протоколы http:// и https://
 * - Домены любого уровня (example.com, sub.domain.example.org)
 * - Опциональный порт (:8080)
 * - Опциональный путь (/path/to/resource)
 * - Не допускает пробелы и угловые скобки в пути
 *
 * @type {RegExp}
 * @example
 * urlRegExp.test('https://example.com'); // true
 * urlRegExp.test('http://sub.domain.com:8080/path'); // true
 * urlRegExp.test('https://example.com/path/to/resource?param=value'); // true
 * urlRegExp.test('https://256.255.25.5/login'); // true
 * urlRegExp.test('ftp://example.com'); // false
 * urlRegExp.test('example.com'); // false
 */
export const urlRegExp = new RegExp(
    '^(https?:\\/\\/)'
  + '((([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,})|'
  + '(\\d{1,3}\\.){3}\\d{1,3})'
  + '(?::\\d{1,5})?'
  + '(?:\\/[^\\s<]*)?$',
);
